import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { Play, Pause, SkipBack, SkipForward } from "@phosphor-icons/react";
import { getReciterName, getSurahAudioUrl, getSurahNameFromCatalog } from "../../lib/audioCatalog";
import BackButton from "../../components/ui/BackButton";
import { useNavigate } from "react-router-dom";

type Props = { reciterId: string; surahNumber: number };

const RECITER_IMAGE: Record<string,string> = {
  "ar.minshawi": "/images/reciters/minshawi.jpg",
  "ar.kazemi": "/images/reciters/kademi.jpg",
  "ar.kjalil": "/images/reciters/kjalil.jpg",
  "ar.mrashad": "/images/reciters/mrashad.jpg",
  "ar.maher": "/images/reciters/maher.jpg",
  "ar.sghamdi": "/images/reciters/sghamdi.jpg",
  "ar.ajmy": "/images/reciters/ajmy.jpg",
  "ar.lahidan": "/images/reciters/lahidan.jpg",
  "ar.mrayan": "/images/reciters/mrayan.jpg",
};

const TOTAL_SURAHS = 114;
const buildSurahHref = (reciterId: string, n: number) => `/listen/${encodeURIComponent(reciterId)}/${n}`;
const msToClock = (ms: number) => { const t = Math.max(0, Math.floor(ms/1000)); const m = Math.floor(t/60); const s = String(t%60).padStart(2,"0"); return `${m}:${s}`; };

export default function FullAudioPlayer({ reciterId, surahNumber }: Props) {
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reciterName = getReciterName(reciterId);

  const [surahName, setSurahName] = useState(getSurahNameFromCatalog(surahNumber) ?? `سورة ${surahNumber}`);
  const [isPlaying, setIsPlaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [previewMs, setPreviewMs] = useState(0);
  const [sliderPx, setSliderPx] = useState(0);

  useEffect(() => { document.title = `${surahName} بصوت ${reciterName} | نور على نور`; }, [surahName, reciterName]);

  const src = useMemo(() => getSurahAudioUrl(reciterId, surahNumber), [reciterId, surahNumber]);
  const imgSrc = RECITER_IMAGE[reciterId] ?? "/images/reciters/placeholder.jpg";

  const goToSurah = useCallback((n: number) => {
    if (n < 1 || n > TOTAL_SURAHS) return;
    try { audioRef.current?.pause(); } catch {}
    navigate(buildSurahHref(reciterId, n));
  }, [reciterId, navigate]);

  const goPrev = useCallback(() => { if (surahNumber > 1) goToSurah(surahNumber - 1); }, [goToSurah, surahNumber]);
  const goNext = useCallback(() => { if (surahNumber < TOTAL_SURAHS) goToSurah(surahNumber + 1); }, [goToSurah, surahNumber]);

  useEffect(() => {
    if (surahName && !/^سورة \d+$/.test(surahName)) return;
    let m = true;
    fetch(`https://api.alquran.cloud/v1/surah/${surahNumber}`)
      .then(r => r.json())
      .then(d => { if (m && d?.data?.name) setSurahName(d.data.name); });
    return () => { m = false; };
  }, [surahNumber, surahName]);

  useEffect(() => {
    if (!src) { setInitializing(false); return; }
    const audio = new Audio(src);
    audioRef.current = audio;
    const onCanPlay = () => { setDuration(Math.round((audio.duration||0)*1000)); setInitializing(false); };
    const onTime = () => setPosition(Math.round(audio.currentTime*1000));
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => { setIsPlaying(false); setPosition(Math.round((audio.duration||0)*1000)); goNext(); };
    const onError = () => { setInitializing(false); setIsPlaying(false); };
    audio.addEventListener("canplay", onCanPlay);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);
    audio.play().catch(() => {});
    return () => {
      audio.pause();
      audio.removeEventListener("canplay", onCanPlay);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
  }, [src, goNext]);

  const safeDuration = Math.max(0, duration||0);
  const safePosition = Math.min(Math.max(0, position||0), safeDuration);
  const remainingMs = Math.max(safeDuration - safePosition, 0);

  async function toggle() {
    if (!audioRef.current) return;
    setBusy(true);
    try { if (isPlaying) await audioRef.current.pause(); else await audioRef.current.play(); }
    finally { setBusy(false); }
  }
  function seekTo(ms:number){ if(!audioRef.current) return; const s=Math.max(0,Math.min(ms,safeDuration))/1000; audioRef.current.currentTime=s; setPosition(Math.round(s*1000)); }

  return (
    <Wrap>
      <TopBar>
        <BackButton href={`/listen/${encodeURIComponent(reciterId)}`} />
        <Title>سورة {surahName}</Title>
      </TopBar>

      <Byline>بصوت: {reciterName}</Byline>

      <ImageBox>
        <img src={imgSrc} alt={reciterName} />
      </ImageBox>

      {initializing && <Loader>جاري التحميل...</Loader>}
      {!src && <Loader>لا يوجد رابط لهذه السورة في هذا القارئ.</Loader>}

      <LTRSection>
        <SliderWrap ref={(el) => { if (!el) return; setSliderPx(el.getBoundingClientRect().width); }}>
          {isScrubbing && (
            <ScrubBubble style={{ left: Math.min(Math.max((previewMs/Math.max(1,safeDuration))*sliderPx - 28, 0), Math.max(sliderPx - 56, 0)) }}>
              {msToClock(previewMs)}
            </ScrubBubble>
          )}
          <input
            type="range"
            min={0}
            max={safeDuration || 1}
            value={isScrubbing ? previewMs : safePosition}
            onChange={(e)=>setPreviewMs(Number(e.target.value))}
            onMouseDown={()=>{ setIsScrubbing(true); setPreviewMs(safePosition); }}
            onMouseUp={()=>{ setIsScrubbing(false); seekTo(previewMs); }}
            onTouchStart={()=>{ setIsScrubbing(true); setPreviewMs(safePosition); }}
            onTouchEnd={()=>{ setIsScrubbing(false); seekTo(previewMs); }}
            disabled={initializing || !safeDuration}
          />
        </SliderWrap>

        <TimeRow>
          <span>{safeDuration ? msToClock(safePosition) : "--:--"}</span>
          <span>{safeDuration ? msToClock(remainingMs) : "--:--"}</span>
        </TimeRow>

        <Controls>
          <IconBtn onClick={goPrev} disabled={initializing || !src || surahNumber<=1} aria-label="السورة السابقة">
            <SkipBack size={28} />
          </IconBtn>

          <PlayBtn onClick={toggle} disabled={busy || initializing || !src} aria-label={isPlaying ? "إيقاف مؤقت" : "تشغيل"}>
            {isPlaying ? <Pause size={40}/> : <Play size={40}/>}
          </PlayBtn>

          <IconBtn onClick={goNext} disabled={initializing || !src || surahNumber>=TOTAL_SURAHS} aria-label="السورة التالية">
            <SkipForward size={28} />
          </IconBtn>
        </Controls>
      </LTRSection>
    </Wrap>
  );
}

const Wrap = styled.div` min-height:100dvh; padding:20px clamp(16px,4vw,32px) 32px; background:var(--background); color:var(--text); display:flex; flex-direction:column; `;
const Title = styled.h1` margin:0 auto; font-size:50px; font-weight:500; color:var(--primary); font-family: Lateef, serif; `;
const TopBar = styled.div` position:relative; display:flex; align-items:center; justify-content:center; min-height:40px; margin-bottom:8px; width:100%; `;
const Byline = styled.p` text-align:center; color:var(--muted); margin:4px 0 16px; `;
const ImageBox = styled.div`
  width:60%; max-width:400px; align-self:center; border-radius:12px; overflow:hidden; margin-bottom:12px; box-shadow:var(--shadow);
  img{ width:100%; object-fit:cover; display:block; }
  @media (min-width:1024px){ width:30%; max-width:240px; }
`;
const Loader = styled.div` text-align:center; padding:24px 0; font-size:1.05rem; `;
const LTRSection = styled.div` direction:ltr !important; unicode-bidi:isolate; width:100%; display:flex; flex-direction:column; align-items:center; `;
const SliderWrap = styled.div`
  position:relative; max-width:520px; width:100%; align-self:center; margin-top:4px; direction:ltr;
  input[type="range"]{ direction:ltr; width:100%; appearance:none; height:3px; border-radius:999px; background:var(--border); outline:none; }
  input[type="range"]::-webkit-slider-thumb{ appearance:none; height:12px; width:12px; border-radius:50%; background:var(--primary); margin-top:-4.5px; cursor:pointer; }
  input[type="range"]::-moz-range-thumb{ height:12px; width:12px; border-radius:50%; background:var(--primary); cursor:pointer; }
`;
const ScrubBubble = styled.div` position:absolute; bottom:24px; width:48px; height:24px; border-radius:12px; background:rgba(0,0,0,.75); color:#fff; font-size:11px; display:grid; place-items:center; pointer-events:none; `;
const TimeRow = styled.div` direction:ltr; display:flex; justify-content:space-between; max-width:520px; width:100%; align-self:center; margin:6px 0 16px; font-weight:700; `;
const Controls = styled.div` direction:ltr; display:flex; gap:24px; align-items:center; justify-content:center; margin-top:8px; `;
const IconBtn = styled.button<{disabled?:boolean}>` height:48px; width:48px; border-radius:999px; border:1px solid var(--border); background:var(--surface); color:var(--text); display:grid; place-items:center; cursor:pointer; opacity:${p=>p.disabled?0.6:1}; `;
const PlayBtn = styled(IconBtn)` height:72px; width:72px; background:var(--primary); color:#fff; border:none; `;
